import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { LynxAlert } from '../../../models/lynx-alert';
import { LynxAlertsService } from '../../../services/iot/lynx-alerts-service';
import { Params } from '../../../services/utilis/add-params-service';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-alerts-list',
  imports: [TableComponent, DialogModule],
  templateUrl: './alerts-list.html',
  styleUrl: './alerts-list.scss',
})
export class AlertsList implements OnInit {

  private alertsService = inject(LynxAlertsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  alertsList = signal<LynxAlert[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;
  searchControl = new FormControl('');

  payloadDialogVisible = false;
  parsedPayload = '';

  params = [
    'page', 'size', 'search', 'sortBy', 'sortDirection',
    'vehicleName', 'alertName', 'lynxAlertId', 'resolutionState',
    'createdFrom', 'createdTo',
    'minSpeed', 'maxSpeed', 'hasPosition',
  ];

  modifiedParams: Params = {
    page: 'page', size: 'size', search: 'search',
    sortBy: 'sortBy', sortDirection: 'sortDirection',
    vehicleName: 'vehicleName', alertName: 'alertName',
    lynxAlertId: 'lynxAlertId', resolutionState: 'resolutionState',
    createdFrom: 'createdFrom', createdTo: 'createdTo',
    minSpeed: 'minSpeed', maxSpeed: 'maxSpeed', hasPosition: 'hasPosition',
  };

  // date-range key 'triggered' → emits triggeredFrom / triggeredTo
  // date-range key 'created'   → emits createdFrom  / createdTo
  customFilters: CustomFilter[] = [
    {
      key: 'alertName',
      label: 'Alert Name',
      type: 'multiselect-param',
      options: [],
    },
    {
      key: 'resolutionState',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'Open', value: 'Open' },
        { label: 'Resolved', value: 'Resolved' },
      ],
    },
    {
      key: 'hasPosition',
      label: 'Has GPS',
      type: 'select',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    {
      key: 'created',
      label: 'Created Date',
      type: 'date-range',
      options: [],
    },
  ];

  tableColumns: DynamicColumn<LynxAlert>[] = [
    { header: 'ID', field: 'id', sortable: true },
    { header: 'Alert ID', field: 'lynxAlertId', sortable: true },
    { header: 'Alert Name', field: 'alertName', sortable: true },
    { header: 'Vehicle', field: 'vehicleName', sortable: true },
    { header: 'Triggered At', field: 'triggeredAt', isDateTime: true, sortable: true },
    { header: 'Resolved At', field: 'resolvedAt', isDateTime: true, sortable: true },
    {
      header: 'Status',
      field: 'resolutionState',
      sortable: true,
      cellClass: (value) => value === 'Open' ? 'text-red-500' : 'text-green-500',
    },
    {
      header: 'Speed (km/h)',
      field: 'speed',
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      sliderKey: 'speed',
      sliderMinKey: 'minSpeed',
      sliderMaxKey: 'maxSpeed',
      min: 0,
      max: 300,
      step: 1,
    },
    { header: 'Created At', field: 'createdAt', isDateTime: true, sortable: true },
    {
      header: 'Raw Payload',
      field: 'rawPayload',
      sortable: false,
      valueGetter: () => '{ ... }',
      command: (alert) => this.openPayload(alert),
    },
    {
      header: 'Location',
      field: 'latitude',
      sortable: true,
      locationCell: true,
      valueGetter: () => null,
      command: (alert) => this.openMap(alert),
    },
  ];

  ngOnInit() {
    this.loadAlerts();
    this.loadAlertNames();
  }

  loadAlertNames() {
    this.alertsService.getAlertNames().subscribe((res) => {
      const alertNameFilter = this.customFilters.find(f => f.key === 'alertName');
      if (alertNameFilter) {
        alertNameFilter.options = res.data.map(name => ({ label: name, value: name }));
      }
    });
  }

  loadAlerts() {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'] ?? '', { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'triggeredAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.alertsService.getAlerts(params);
      })
    ).subscribe((res) => {
      this.alertsList.set(res.data.content);
      this.totalRecords.set(res.data.totalElements);
      this.first = res.data.currentPage * res.data.pageSize;
    });
  }

  onPageChange(event: PaginatorState) {
    this.router.navigate([], {
      queryParams: { page: event.page, size: event.rows },
      queryParamsHandling: 'merge',
    });
  }

  openPayload(alert: LynxAlert) {
    try {
      const parsed = typeof alert.rawPayload === 'string'
        ? JSON.parse(alert.rawPayload)
        : alert.rawPayload;
      this.parsedPayload = JSON.stringify(parsed, null, 2);
    } catch {
      this.parsedPayload = alert.rawPayload ?? '';
    }
    this.payloadDialogVisible = true;
  }

  openMap(alert: LynxAlert) {
    if (alert.latitude && alert.longitude) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${alert.latitude},${alert.longitude}`,
        '_blank'
      );
    }
  }
}
